import { Component, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { RegisterUserModel } from '../../../models/user.model';
import { AuthService } from '../../../services/auth.service';
import { fieldRequired } from '../../../utils/form.utils';
import { passwordRequirementsValidator } from '../../../validators/password-requirements.validator';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  standalone: false,
})
export class RegisterComponent implements OnDestroy {
  passwordMismatch = false;
  public showPassword = false;
  public showPasswordCheck = false;
  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private authService: AuthService
  ) {
    this.createForm();
  }

  private destroy$ = new Subject<void>();

  // Variables
  public registerForm: FormGroup;

  public register() {
    if (!this.registerForm.valid) {
      this.registerForm.markAllAsTouched();
      console.error('Form not valid');
      return;
    }
    const registerUser: RegisterUserModel = {
      firstname: this.registerForm.value.firstname,
      lastname: this.registerForm.value.lastname,
      email: this.registerForm.value.email,
      password: this.registerForm.value.password,
      newsletterOptIn: this.registerForm.value.newsletterOptIn,
    };

    this.authService
      .register(registerUser)
      .then(() => {
        this.router.navigate(['/verify']);
      })
      .catch(error => {
        console.error('Registration error:', error);
      });
  }

  public route(route: string) {
    this.router.navigate([route]);
  }

  public fieldRequired = fieldRequired;

  private createForm() {
    this.registerForm = this.formBuilder.group({
      firstname: new FormControl('', Validators.required),
      lastname: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [
        Validators.required,
        passwordRequirementsValidator(),
      ]),
      passwordCheck: new FormControl('', [
        Validators.required,
        passwordRequirementsValidator(),
      ]),
      agbConfirmation: [false, Validators.requiredTrue],
      newsletterOptIn: [false],
    });
    this.verifyPasswordsMatch();
  }

  // subscribe to the valueChanges of the password and passwordCheck fields
  private verifyPasswordsMatch() {
    this.registerForm
      .get('passwordCheck')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        const password = this.registerForm.get('password').value;
        if (password !== value) {
          this.passwordMismatch = true;
          this.registerForm.get('passwordCheck').setErrors({ mismatch: true });
        } else {
          this.passwordMismatch = false;
        }
      });
    this.registerForm
      .get('password')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        if (!this.registerForm.get('passwordCheck').touched) {
          return;
        }
        const passwordCheck = this.registerForm.get('passwordCheck').value;
        if (passwordCheck !== value) {
          this.passwordMismatch = true;
          this.registerForm.get('passwordCheck').setErrors({ mismatch: true });
        } else {
          this.passwordMismatch = false;
        }
      });
  }

  public togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  public togglePasswordCheckVisibility() {
    this.showPasswordCheck = !this.showPasswordCheck;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
